/**
 * Avatar — shared types, Zod prop schema, and tokens.
 *
 * A circular profile picture (Figma "Profile picture"). Renders the supplied
 * image cover-cropped when `imageUri` is set, or the host-registered bundled
 * asset when `imageAssetKey` names one, otherwise a placeholder circle with a
 * person glyph. Presentational; the host supplies the image and label.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';

export const avatarPropsSchema = z.object({
  imageUri: z.string().optional(),
  /**
   * Semantic key for a host-registered bundled image, resolved through
   * `avatar-asset-registry`. The document-authorable counterpart to `imageUri`:
   * an authored surface names the image rather than carrying a URL, so a
   * server-composed document stays free of transport detail. Ignored when
   * `imageUri` is set; an unregistered key falls through to the placeholder.
   */
  imageAssetKey: z.string().optional(),
  initials: z.string().trim().min(1).max(3).optional(),
  label: z.string().optional(),
  size: z.number().optional(),
  /**
   * Short attention text (a count like "1" or "9+") rendered as a small circle
   * overlapping the circle's top-right corner. Decorative at the element level:
   * the badge text is hidden from assistive technology, so a host that needs
   * the state announced carries it in `label`.
   */
  badge: z.string().trim().min(1).max(3).optional(),
  testID: z.string().optional(),
});

export type AvatarProps = z.infer<typeof avatarPropsSchema>;

/** Default circle diameter (Figma "Profile picture" = 48). */
export const AVATAR_DEFAULT_SIZE = 48;
/** Placeholder circle fill + glyph (existing avatar/icon tokens). */
export const AVATAR_PLACEHOLDER_BACKGROUND = colors.avatar.background;
export const AVATAR_PLACEHOLDER_ICON_COLOR = colors.icon.highlight;
export const AVATAR_PLACEHOLDER_ICON: IconName = 'profile-filled';
export const AVATAR_INITIALS_TYPOGRAPHY = typography.c2Regular;
export const AVATAR_INITIALS_COLOR = colors.text.primary;
/** Initials glyph height as a fraction of the circle, so two letters fit a compact avatar. */
export const AVATAR_INITIALS_RATIO = 0.42;
/**
 * The initials type for a circle of `size`: the caption token wherever it
 * fits, scaled down (line height with it) on circles too small to hold two
 * letters at that size.
 */
export type AvatarInitialsTypography = Omit<
  typeof AVATAR_INITIALS_TYPOGRAPHY,
  'fontSize' | 'lineHeight'
> & { readonly fontSize: number; readonly lineHeight: number };

export function avatarInitialsTypography(size: number): AvatarInitialsTypography {
  const fontSize = Math.min(
    AVATAR_INITIALS_TYPOGRAPHY.fontSize,
    Math.round(size * AVATAR_INITIALS_RATIO),
  );
  if (fontSize === AVATAR_INITIALS_TYPOGRAPHY.fontSize) return AVATAR_INITIALS_TYPOGRAPHY;
  const scale = fontSize / AVATAR_INITIALS_TYPOGRAPHY.fontSize;
  return {
    ...AVATAR_INITIALS_TYPOGRAPHY,
    fontSize,
    lineHeight: Math.round(AVATAR_INITIALS_TYPOGRAPHY.lineHeight * scale),
  };
}
/**
 * Glyph diameter as a fraction of the circle. Smaller than Figma's 42/48 ratio
 * because our `profile-filled` icon fills its viewBox more tightly (shoulders
 * ~92% of the box) than the Figma asset, so a smaller box matches the figure
 * width. Bottom-anchored, so the circle clips the shoulders.
 */
export const AVATAR_GLYPH_RATIO = 0.75;
/** Badge circle fill + text (attention/alert tokens, matching Label's alert use). */
export const AVATAR_BADGE_BACKGROUND = colors.text.alert;
export const AVATAR_BADGE_TEXT_COLOR = colors.text.inverted;
export const AVATAR_BADGE_TYPOGRAPHY = typography.c1Medium;
/** Badge circle diameter as a fraction of the circle, floored so text stays legible. */
export const AVATAR_BADGE_RATIO = 0.3;
export const AVATAR_BADGE_MIN_HEIGHT = 20;
export function avatarBadgeHeight(size: number): number {
  return Math.max(AVATAR_BADGE_MIN_HEIGHT, Math.round(size * AVATAR_BADGE_RATIO));
}
